export type JwtHeader = {
  alg: string
  typ: string
  [key: string]: unknown
}

export type JwtPayload = {
  sub?: string
  iss?: string
  aud?: string | string[]
  exp?: number
  iat?: number
  nbf?: number
  jti?: string
  [key: string]: unknown
}

export type DecodedJwt<T extends JwtPayload = JwtPayload> = {
  header: JwtHeader
  payload: T
  signature: string
}

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  )

  try {
    return decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    )
  } catch {
    return atob(padded)
  }
}

/**
 * Decodes a JWT token without verifying its signature.
 * Use only on the client side - never trust decoded data for authorization.
 *
 * @param token - The raw JWT string
 * @returns The decoded header, payload and raw signature, or `null` if the token is malformed
 */
export function decodeJwt<T extends JwtPayload = JwtPayload>(
  token: string
): DecodedJwt<T> | null {
  if (!token || typeof token !== 'string') return null

  const parts = token.split('.')

  if (parts.length !== 3) return null

  try {
    const header = JSON.parse(base64UrlDecode(parts[0])) as JwtHeader
    const payload = JSON.parse(base64UrlDecode(parts[1])) as T
    const signature = parts[2]

    return { header, payload, signature }
  } catch {
    return null
  }
}

/**
 * Returns true if the JWT token is expired based on the `exp` claim.
 * Returns false if the token has no `exp` claim or is malformed.
 */
export function isJwtExpired(token: string): boolean {
  const decoded = decodeJwt(token)

  if (!decoded?.payload.exp) return false

  return Date.now() >= decoded.payload.exp * 1000
}
